import React, { useState } from 'react';
// Removed react-icons import to avoid TypeScript issues

interface Table1Data {
  title: string;
  company: string;
  headers: string[];
  rows: Array<{
    // Single brand format
    productName?: string;
    os?: number;
    purchase?: number;
    sales?: number;
    cs?: number;
    productNo?: string;
    entryId?: string | null;
    // Dual brand format (legacy)
    aquafitProductName?: string;
    aquafitOS?: number;
    aquafitPurchase?: number;
    aquafitSales?: number;
    aquafitCS?: number;
    aquafitProductNo?: string;
    aquafitEntryId?: string | null;
    cablefitProductName?: string;
    cablefitOS?: number;
    cablefitPurchase?: number;
    cablefitSales?: number;
    cablefitCS?: number;
    cablefitProductNo?: string;
    cablefitEntryId?: string | null;
  }>;
}

interface InventoryTable1Props {
  data: Table1Data;
  canEdit: boolean;
  onUpdate: (updates: any[]) => void;
  saving: boolean;
}

const InventoryTable1: React.FC<InventoryTable1Props> = ({ data, canEdit, onUpdate, saving }) => {
  const [editingCell, setEditingCell] = useState<{ rowIndex: number; field: string; brand?: 'aquafit' | 'cablefit' } | null>(null);
  const [editValue, setEditValue] = useState<string>('');
  const [pendingUpdates, setPendingUpdates] = useState<any[]>([]);

  // Detect if this is single brand or dual brand format
  const isSingleBrand = data.rows.length > 0 && data.rows[0].productName !== undefined;
  const brandName = isSingleBrand ? data.headers[0] : 'AQUAFIT';

  const handleEditStart = (rowIndex: number, field: 'purchase' | 'sales', brand?: 'aquafit' | 'cablefit', currentValue?: number) => {
    if (!canEdit) return;
    setEditingCell({ rowIndex, field, brand });
    setEditValue(currentValue?.toString() || '0');
  };

  const handleEditCancel = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const handleEditSave = () => {
    if (!editingCell) return;

    const { rowIndex, field, brand } = editingCell;
    const newValue = parseFloat(editValue) || 0;

    // Add to pending updates
    const existingUpdateIndex = pendingUpdates.findIndex(
      update => update.rowIndex === rowIndex && update.brand === brand
    );

    const row = data.rows[rowIndex];
    const updateData = {
      rowIndex,
      brand,
      field,
      value: newValue,
      productNo: isSingleBrand 
        ? row.productNo 
        : (brand === 'aquafit' ? row.aquafitProductNo : row.cablefitProductNo),
      entryId: isSingleBrand 
        ? row.entryId 
        : (brand === 'aquafit' ? row.aquafitEntryId : row.cablefitEntryId)
    };

    if (existingUpdateIndex >= 0) {
      const updatedUpdates = [...pendingUpdates];
      updatedUpdates[existingUpdateIndex] = { ...updatedUpdates[existingUpdateIndex], [field]: newValue };
      setPendingUpdates(updatedUpdates);
    } else {
      setPendingUpdates([...pendingUpdates, updateData]);
    }

    setEditingCell(null);
    setEditValue('');
  };

  const handleSaveAll = () => {
    if (pendingUpdates.length === 0) return;

    const updates = pendingUpdates.map(update => {
      const row = data.rows[update.rowIndex];
      return {
        productNo: update.productNo,
        purchase: update.field === 'purchase' ? update.value : (
          isSingleBrand 
            ? (row.purchase || 0) 
            : (update.brand === 'aquafit' ? (row.aquafitPurchase || 0) : (row.cablefitPurchase || 0))
        ),
        sales: update.field === 'sales' ? update.value : (
          isSingleBrand 
            ? (row.sales || 0) 
            : (update.brand === 'aquafit' ? (row.aquafitSales || 0) : (row.cablefitSales || 0))
        ),
        rate: 0 // Will be calculated by backend
      };
    });

    onUpdate(updates);
    setPendingUpdates([]);
  };

  const getCellValue = (rowIndex: number, field: string, brand?: 'aquafit' | 'cablefit') => {
    const pendingUpdate = pendingUpdates.find(
      update => update.rowIndex === rowIndex && update.brand === brand && update.field === field
    );
    
    if (pendingUpdate) {
      return pendingUpdate.value;
    }
    
    const row = data.rows[rowIndex];
    
    if (isSingleBrand) {
      if (field === 'purchase') return row.purchase || 0;
      if (field === 'sales') return row.sales || 0;
    } else {
      if (brand === 'aquafit') {
        if (field === 'purchase') return row.aquafitPurchase || 0;
        if (field === 'sales') return row.aquafitSales || 0;
      } else if (brand === 'cablefit') {
        if (field === 'purchase') return row.cablefitPurchase || 0;
        if (field === 'sales') return row.cablefitSales || 0;
      }
    }
    return 0;
  };

  const isEditing = (rowIndex: number, field: string, brand?: 'aquafit' | 'cablefit') => {
    return editingCell?.rowIndex === rowIndex && editingCell?.field === field && editingCell?.brand === brand;
  };

  const hasPendingUpdates = pendingUpdates.length > 0;

  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-blue-700 px-6 py-4">
        <h2 className="text-xl font-semibold text-white">Table 1: Detailed Transaction Table</h2>
        <p className="text-blue-100 text-sm">{data.title} - {data.company}</p>
      </div>

      {/* Action Bar */}
      {canEdit && hasPendingUpdates && (
        <div className="bg-yellow-50 border-b border-yellow-200 px-6 py-3">
          <div className="flex items-center justify-between">
            <span className="text-yellow-800 text-sm font-medium">
              {pendingUpdates.length} pending changes
            </span>
            <div className="flex space-x-2">
              <button
                onClick={() => setPendingUpdates([])}
                className="px-3 py-1 text-xs bg-gray-500 text-white rounded hover:bg-gray-600"
              >
                Cancel All
              </button>
              <button
                onClick={handleSaveAll}
                disabled={saving}
                className="px-3 py-1 text-xs bg-green-600 text-white rounded hover:bg-green-700 disabled:opacity-50 flex items-center"
              >
                ✓
                {saving ? 'Saving...' : 'Save All'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              {data.headers.map((header, index) => (
                <th 
                  key={index}
                  className={`px-6 py-3 text-xs font-medium text-gray-500 uppercase tracking-wider ${
                    index === 0 || (index === 5 && !isSingleBrand) ? 'text-left' : 'text-center'
                  }`}
                >
                  {header}
                </th>
              ))}
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {data.rows.map((row, rowIndex) => (
              <tr key={rowIndex} className="hover:bg-gray-50">
                {isSingleBrand ? (
                  // Single brand format
                  <>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="font-medium">{row.productName}</div>
                      <div className="text-xs text-gray-500">{row.productNo}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      {((row.os || 0)).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {isEditing(rowIndex, 'purchase') ? (
                        <div className="flex items-center space-x-1">
                          <input
                            type="number"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="w-20 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            autoFocus
                          />
                          <button
                            onClick={handleEditSave}
                            className="text-green-600 hover:text-green-800"
                          >
                            ✓
                          </button>
                          <button
                            onClick={handleEditCancel}
                            className="text-red-600 hover:text-red-800"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center space-x-1">
                          <span className="text-sm text-gray-900">
                            {(getCellValue(rowIndex, 'purchase') || 0).toLocaleString()}
                          </span>
                          {canEdit && (
                            <button
                              onClick={() => handleEditStart(rowIndex, 'purchase', undefined, getCellValue(rowIndex, 'purchase'))}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              ✏️
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {isEditing(rowIndex, 'sales') ? (
                        <div className="flex items-center space-x-1">
                          <input
                            type="number"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="w-20 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            autoFocus
                          />
                          <button
                            onClick={handleEditSave}
                            className="text-green-600 hover:text-green-800"
                          >
                            ✓
                          </button>
                          <button
                            onClick={handleEditCancel}
                            className="text-red-600 hover:text-red-800"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center space-x-1">
                          <span className="text-sm text-gray-900">
                            {(getCellValue(rowIndex, 'sales') || 0).toLocaleString()}
                          </span>
                          {canEdit && (
                            <button
                              onClick={() => handleEditStart(rowIndex, 'sales', undefined, getCellValue(rowIndex, 'sales'))}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              ✏️
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      {((row.cs || 0)).toLocaleString()}
                    </td>
                  </>
                ) : (
                  // Dual brand format (legacy)
                  <>
                    {/* AQUAFIT Column */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="font-medium">{row.aquafitProductName}</div>
                      <div className="text-xs text-gray-500">{row.aquafitProductNo}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      {(row.aquafitOS || 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {isEditing(rowIndex, 'purchase', 'aquafit') ? (
                        <div className="flex items-center space-x-1">
                          <input
                            type="number"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="w-20 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            autoFocus
                          />
                          <button
                            onClick={handleEditSave}
                            className="text-green-600 hover:text-green-800"
                          >
                            ✓
                          </button>
                          <button
                            onClick={handleEditCancel}
                            className="text-red-600 hover:text-red-800"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center space-x-1">
                          <span className="text-sm text-gray-900">
                            {getCellValue(rowIndex, 'purchase', 'aquafit').toLocaleString()}
                          </span>
                          {canEdit && (
                            <button
                              onClick={() => handleEditStart(rowIndex, 'purchase', 'aquafit', getCellValue(rowIndex, 'purchase', 'aquafit'))}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              ✏️
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {isEditing(rowIndex, 'sales', 'aquafit') ? (
                        <div className="flex items-center space-x-1">
                          <input
                            type="number"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="w-20 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            autoFocus
                          />
                          <button
                            onClick={handleEditSave}
                            className="text-green-600 hover:text-green-800"
                          >
                            ✓
                          </button>
                          <button
                            onClick={handleEditCancel}
                            className="text-red-600 hover:text-red-800"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center space-x-1">
                          <span className="text-sm text-gray-900">
                            {getCellValue(rowIndex, 'sales', 'aquafit').toLocaleString()}
                          </span>
                          {canEdit && (
                            <button
                              onClick={() => handleEditStart(rowIndex, 'sales', 'aquafit', getCellValue(rowIndex, 'sales', 'aquafit'))}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              ✏️
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      {(row.aquafitCS || 0).toLocaleString()}
                    </td>

                    {/* CABLEFIT Column */}
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      <div className="font-medium">{row.cablefitProductName}</div>
                      <div className="text-xs text-gray-500">{row.cablefitProductNo}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      {(row.cablefitOS || 0).toLocaleString()}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {isEditing(rowIndex, 'purchase', 'cablefit') ? (
                        <div className="flex items-center space-x-1">
                          <input
                            type="number"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="w-20 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            autoFocus
                          />
                          <button
                            onClick={handleEditSave}
                            className="text-green-600 hover:text-green-800"
                          >
                            ✓
                          </button>
                          <button
                            onClick={handleEditCancel}
                            className="text-red-600 hover:text-red-800"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center space-x-1">
                          <span className="text-sm text-gray-900">
                            {getCellValue(rowIndex, 'purchase', 'cablefit').toLocaleString()}
                          </span>
                          {canEdit && (
                            <button
                              onClick={() => handleEditStart(rowIndex, 'purchase', 'cablefit', getCellValue(rowIndex, 'purchase', 'cablefit'))}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              ✏️
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-center">
                      {isEditing(rowIndex, 'sales', 'cablefit') ? (
                        <div className="flex items-center space-x-1">
                          <input
                            type="number"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            className="w-20 px-2 py-1 text-sm border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
                            autoFocus
                          />
                          <button
                            onClick={handleEditSave}
                            className="text-green-600 hover:text-green-800"
                          >
                            ✓
                          </button>
                          <button
                            onClick={handleEditCancel}
                            className="text-red-600 hover:text-red-800"
                          >
                            ✕
                          </button>
                        </div>
                      ) : (
                        <div className="flex items-center justify-center space-x-1">
                          <span className="text-sm text-gray-900">
                            {getCellValue(rowIndex, 'sales', 'cablefit').toLocaleString()}
                          </span>
                          {canEdit && (
                            <button
                              onClick={() => handleEditStart(rowIndex, 'sales', 'cablefit', getCellValue(rowIndex, 'sales', 'cablefit'))}
                              className="text-blue-600 hover:text-blue-800"
                            >
                              ✏️
                            </button>
                          )}
                        </div>
                      )}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 text-center">
                      {(row.cablefitCS || 0).toLocaleString()}
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="bg-gray-50 px-6 py-3">
        <p className="text-xs text-gray-600">
          {canEdit ? 'Click the edit icon to modify Purchase and Sales values' : 'Read-only view'}
        </p>
      </div>
    </div>
  );
};

export default InventoryTable1;
